import React, { useState, useEffect, useCallback, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getData } from "../../../../services/Fetchnodeservices";
import { usePermissions } from "../../../../hooks/usePermissions";
import { ThemeContext } from "../../../../context/ThemeContext";
import OrderTrackingModal from "./OrderTrackingModal";

// ─────────────────────────────────────────────────────────────────────────────
// EXPORT LIBRARIES - Install: npm install jspdf jspdf-autotable xlsx file-saver
// ─────────────────────────────────────────────────────────────────────────────
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// ─────────────────────────────────────────────────────────────────────────────
// ICONS
// ─────────────────────────────────────────────────────────────────────────────
const SearchIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);

const DetailsIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
    <line x1="12" y1="11" x2="12" y2="15"/><line x1="10" y1="13" x2="14" y2="13"/>
  </svg>
);

const TrackIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
    <path d="M12 2v4M22 10h-4M2 10h4M12 18v4"/>
  </svg>
);

const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const BackIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);

const DownloadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const PDFIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="12" y1="18" x2="12" y2="12"/>
    <line x1="9" y1="15" x2="15" y2="15"/>
  </svg>
);

const ExcelIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="8" y1="16" x2="16" y2="16"/>
    <line x1="8" y1="12" x2="16" y2="12"/>
    <line x1="8" y1="8" x2="10" y2="8"/>
  </svg>
);

// ─────────────────────────────────────────────────────────────────────────────
// DATE FORMAT HELPER
// ─────────────────────────────────────────────────────────────────────────────
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "N/A";
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatTime = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS (Theme-Aware)
// ─────────────────────────────────────────────────────────────────────────────
const StatusBadge = ({ status, theme }) => {
  const getConfig = () => {
    const s = status?.toLowerCase();
    if (s === "completed" || s === "delivered") return { bg: `${theme.success}1a`, color: theme.success, label: s === "completed" ? "Completed" : "Delivered" };
    if (s === "pending" || s === "new") return { bg: `${theme.warning}1a`, color: theme.warning, label: s === "pending" ? "Pending" : "New" };
    if (s === "cancelled" || s === "dropped") return { bg: `${theme.error}1a`, color: theme.error, label: s === "cancelled" ? "Cancelled" : "Dropped" };
    if (["accepted", "preparing", "ready", "out_for_delivery", "picked", "active"].includes(s)) {
      return { bg: `${theme.primary}1a`, color: theme.primary, label: s === "out_for_delivery" ? "Out for Delivery" : s.charAt(0).toUpperCase() + s.slice(1) };
    }
    return { bg: `${theme.textMuted}1a`, color: theme.textMuted, label: status || "Unknown" };
  };
  const c = getConfig();
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: "16px",
      fontSize: "9px", fontWeight: 600, background: c.bg, color: c.color,
      transition: "all 0.3s ease"
    }}>{c.label}</span>
  );
};

const PayStatusBadge = ({ status, theme }) => {
  const config = {
    paid: { bg: `${theme.success}1a`, color: theme.success, label: "Paid" },
    pending: { bg: `${theme.warning}1a`, color: theme.warning, label: "Pending" },
  };
  const c = config[status?.toLowerCase()] || config.pending;
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: "16px",
      fontSize: "9px", fontWeight: 600, background: c.bg, color: c.color,
      transition: "all 0.3s ease"
    }}>{c.label}</span>
  );
};

const Checkbox = ({ checked, onChange, theme }) => (
  <div onClick={onChange} style={{
    width: "14px", height: "14px", borderRadius: "3px",
    border: checked ? `2px solid ${theme.primary}` : `2px solid ${theme.border}`,
    background: checked ? theme.primary : "transparent",
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", transition: "all 0.15s ease",
  }}>
    {checked && (
      <svg width="8" height="8" viewBox="0 0 12 12" fill="none">
        <path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    )}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT - OrderTable
// ─────────────────────────────────────────────────────────────────────────────
const OrderTable = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const { theme } = useContext(ThemeContext);
  
  const canViewOrders = hasPermission('orders.view');
  const canEditOrders = hasPermission('orders.edit');
  const canTrackOrders = hasPermission('orders.order_tracking_model');
  const canExportOrders = hasPermission('orders.export') || canViewOrders;
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [paymentFilter, setPaymentFilter] = useState("All");
  const [selected, setSelected] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const itemsPerPage = 10;
  const [quickFilter, setQuickFilter] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [isPreFiltered, setIsPreFiltered] = useState(false);
  const [filterTitle, setFilterTitle] = useState("");
  const [exporting, setExporting] = useState(false);
  const cancelledAmount = Number(location.state?.cancelledAmount || 0);
  const showCancelledAmount = location.state?.type === "cancelled";
  const formatCurrency = (amount) => Number(amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  const getOrderStatus = (order) => {
    if (order.order_status_driver === "delivered") return "Delivered";
    if (order.order_status_restro === "cancelled") return "Cancelled";
    if (order.order_status_restro === "completed") return "Completed";
    if (order.order_status_restro === "accepted") return "Active";
    if (order.order_status_restro === "preparing") return "Preparing";
    if (order.order_status_restro === "ready") return "Ready";
    if (order.order_status_driver === "out_for_delivery") return "Out for Delivery";
    if (order.order_status_driver === "picked") return "Picked";
    return "New";
  };

  const fetchOrders = useCallback(async () => {
    if (!canViewOrders) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const state = location.state;
      if (state && state.data && state.data.length > 0) {
        const sortedData = [...state.data].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        const transformedOrders = sortedData.map(order => ({
          ...order,
          orderId: `#${order.id}`,
          status: getOrderStatus(order),
          restaurant: order.organisation_name || order.designation || "Restaurant",
          user: order.receiver_name || `User ${order.userid}`,
          amount: order.final_amount || order.price,
          date: formatDate(order.created_at),
          time: formatTime(order.created_at)
        }));
        setOrders(transformedOrders);
        setIsPreFiltered(true);
        setFilterTitle(state.title || "Filtered Orders");
        setLoading(false);
        return;
      }

      const response = await getData("orders/Orders");
      let ordersData = [];
      if (Array.isArray(response)) ordersData = response;
      else if (response?.data && Array.isArray(response.data)) ordersData = response.data;
      else if (response?.orders && Array.isArray(response.orders)) ordersData = response.orders;
      
      ordersData = ordersData.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      const transformedOrders = ordersData.map(order => ({
        ...order,
        orderId: `#${order.id}`,
        status: getOrderStatus(order),
        restaurant: order.organisation_name || order.designation || "Restaurant",
        user: order.receiver_name || `User ${order.userid}`,
        amount: order.final_amount || order.price,
        date: formatDate(order.created_at),
        time: formatTime(order.created_at)
      }));
      setOrders(transformedOrders);
      setIsPreFiltered(false);
      setFilterTitle("");
    } catch (error) { 
      console.error("Error fetching orders:", error); 
    } finally { 
      setLoading(false); 
    }
  }, [location.state, canViewOrders]);

  useEffect(() => { 
    fetchOrders(); 
  }, [fetchOrders]);

  const matchesQuickFilter = (order) => {
    switch (quickFilter) {
      case "all": return true;
      case "new": return order.order_status_restro === "pending" || !order.order_status_restro;
      case "accepted_preparing_out": return ["accepted", "preparing", "ready", "out_for_delivery", "picked"].includes(order.order_status_restro) || order.order_status_driver === "out_for_delivery";
      case "delivered": return order.order_status_driver === "delivered";
      case "cancelled": return order.order_status_restro === "cancelled";
      default: return true;
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.orderId.toLowerCase().includes(search.toLowerCase()) ||
      order.restaurant?.toLowerCase().includes(search.toLowerCase()) ||
      order.user?.toLowerCase().includes(search.toLowerCase()) ||
      order.receiver_phone?.includes(search);
    const matchesStatus = statusFilter === "All" || order.status === statusFilter;
    const matchesPayment = paymentFilter === "All" || order.pay_method === paymentFilter;
    const matchesQuick = matchesQuickFilter(order);
    return matchesSearch && matchesStatus && matchesPayment && matchesQuick;
  });

  // ─── GET DATA FOR EXPORT ───
  const getExportData = () => {
    return filteredOrders.map(order => ({
      'Order ID': order.orderId || `#${order.id}`,
      'Status': order.status || 'N/A',
      'Date': order.date || 'N/A',
      'Time': order.time || 'N/A',
      'Restaurant': order.restaurant || order.organisation_name || 'N/A',
      'Customer': order.user || order.receiver_name || 'N/A',
      'Phone': order.receiver_phone || order.phone || 'N/A',
      'Payment Status': order.pay_status || 'N/A',
      'Payment Method': order.pay_method || 'COD',
      'Restro Status': order.order_status_restro || 'N/A',
      'Driver Status': order.order_status_driver || 'N/A',
      'Amount (₹)': order.amount || order.final_amount || order.price || 0,
      'Order Type': order.order_type || 'N/A',
      'Created At': order.created_at ? new Date(order.created_at).toLocaleString() : 'N/A'
    }));
  };

  // ─── EXPORT TO PDF ───
  const exportToPDF = async () => {
    if (!canExportOrders) {
      alert("You don't have permission to export orders");
      return;
    }

    setExporting(true);
    try {
      const data = getExportData();
      if (data.length === 0) {
        alert("No orders to export!");
        setExporting(false);
        return;
      }

      const doc = new jsPDF('l', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      // Header
      doc.setFontSize(18);
      doc.setTextColor(40, 40, 40);
      const title = isPreFiltered ? filterTitle : 'Orders Report';
      doc.text(title, pageWidth / 2, 15, { align: 'center' });
      
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Exported: ${new Date().toLocaleString()}`, pageWidth / 2, 22, { align: 'center' });
      
      doc.setFontSize(9);
      doc.setTextColor(80, 80, 80);
      doc.text(`Total Orders: ${data.length}`, 14, 30);
      
      const headers = Object.keys(data[0]);
      const rows = data.map(item => Object.values(item));

      autoTable(doc, {
        head: [headers],
        body: rows,
        startY: 35,
        styles: { 
          fontSize: 6, 
          cellPadding: 2,
          textColor: [50, 50, 50]
        },
        headStyles: { 
          fillColor: [52, 73, 94], 
          textColor: [255, 255, 255],
          fontSize: 7,
          fontStyle: 'bold',
          halign: 'center',
          cellPadding: 2
        },
        columnStyles: {
          0: { cellWidth: 15 },
          1: { cellWidth: 15 },
          2: { cellWidth: 13 },
          3: { cellWidth: 12 },
          4: { cellWidth: 22 },
          5: { cellWidth: 18 },
          6: { cellWidth: 15 },
          7: { cellWidth: 15 },
          8: { cellWidth: 15 },
          9: { cellWidth: 15 },
          10: { cellWidth: 15 },
          11: { cellWidth: 15 },
          12: { cellWidth: 15 },
          13: { cellWidth: 22 },
        },
        margin: { left: 10, right: 10 },
        pageBreak: 'auto',
        didDrawPage: function(data) {
          doc.setFontSize(7);
          doc.setTextColor(150, 150, 150);
          doc.text(
            `Page ${data.pageNumber} of ${doc.internal.getNumberOfPages()}`,
            pageWidth / 2,
            pageHeight - 8,
            { align: 'center' }
          );
        }
      });

      const fileName = `${isPreFiltered ? filterTitle : 'Orders'}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Failed to export PDF. Please check console for details.');
    } finally {
      setExporting(false);
    }
  };

  // ─── EXPORT TO EXCEL ───
  const exportToExcel = () => {
    if (!canExportOrders) {
      alert("You don't have permission to export orders");
      return;
    }

    setExporting(true);
    try {
      const data = getExportData();
      if (data.length === 0) {
        alert("No orders to export!");
        setExporting(false);
        return;
      }

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(data);
      
      ws['!cols'] = [
        { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 12 },
        { wch: 25 }, { wch: 20 }, { wch: 18 }, { wch: 18 },
        { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 15 },
        { wch: 18 }, { wch: 25 }
      ];

      XLSX.utils.book_append_sheet(wb, ws, 'Orders');
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const fileName = `${isPreFiltered ? filterTitle : 'Orders'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      saveAs(blob, fileName);
    } catch (error) {
      console.error('Error exporting Excel:', error);
      alert('Failed to export Excel. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // ─── EXPORT SELECTED ORDERS ───
  const exportSelected = () => {
    if (selected.size === 0) {
      alert("Please select at least one order to export.");
      return;
    }

    const selectedOrders = orders.filter(order => selected.has(order.id));
    const exportData = selectedOrders.map(order => ({
      'Order ID': order.orderId || `#${order.id}`,
      'Status': order.status || 'N/A',
      'Date': order.date || 'N/A',
      'Time': order.time || 'N/A',
      'Restaurant': order.restaurant || order.organisation_name || 'N/A',
      'Customer': order.user || order.receiver_name || 'N/A',
      'Phone': order.receiver_phone || order.phone || 'N/A',
      'Payment Status': order.pay_status || 'N/A',
      'Payment Method': order.pay_method || 'COD',
      'Restro Status': order.order_status_restro || 'N/A',
      'Driver Status': order.order_status_driver || 'N/A',
      'Amount (₹)': order.amount || order.final_amount || order.price || 0,
      'Order Type': order.order_type || 'N/A',
      'Created At': order.created_at ? new Date(order.created_at).toLocaleString() : 'N/A'
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);
    ws['!cols'] = [
      { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 12 },
      { wch: 25 }, { wch: 20 }, { wch: 18 }, { wch: 18 },
      { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 15 },
      { wch: 18 }, { wch: 25 }
    ];
    XLSX.utils.book_append_sheet(wb, ws, 'Selected Orders');
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    const fileName = `Selected_Orders_${new Date().toISOString().split('T')[0]}.xlsx`;
    saveAs(blob, fileName);
  };

  const totalFilteredCount = filteredOrders.length;
  const totalPages = Math.ceil(totalFilteredCount / itemsPerPage);
  const paginatedOrders = filteredOrders.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // ─── PAGINATION FUNCTIONS ───
  const goToFirstPage = () => setCurrentPage(1);
  const goToLastPage = () => setCurrentPage(totalPages);

  const toggleAll = () => {
    if (selected.size === paginatedOrders.length && paginatedOrders.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(paginatedOrders.map(o => o.id)));
    }
  };
  
  const toggleOne = (id) => { 
    const newSelected = new Set(selected); 
    newSelected.has(id) ? newSelected.delete(id) : newSelected.add(id); 
    setSelected(newSelected); 
  };

  const getQuickFilterCounts = () => ({
    all: orders.length,
    new: orders.filter(o => o.order_status_restro === "pending" || !o.order_status_restro).length,
    accepted_preparing_out: orders.filter(o => ["accepted", "preparing", "ready", "out_for_delivery", "picked"].includes(o.order_status_restro) || o.order_status_driver === "out_for_delivery").length,
    delivered: orders.filter(o => o.order_status_driver === "delivered").length,
    cancelled: orders.filter(o => o.order_status_restro === "cancelled").length,
  });
  const counts = getQuickFilterCounts();

  const handleViewFullDetails = (order) => {
    navigate("/dashboard/orders/detail", { 
      state: { 
        order: { ...order, restaurant: order.restaurant || order.organisation_name || order.designation || "Restaurant" },
        from: location.pathname
      } 
    });
  };

  const handleViewFullDetails2 = (order) => {
    if (!canEditOrders) {
      alert("You don't have permission to edit orders");
      return;
    }
    navigate("/dashboard/order-detail", { 
      state: { 
        order: { ...order, restaurant: order.restaurant || order.organisation_name || order.designation || "Restaurant" },
        from: location.pathname
      } 
    });
  };
  
  const handleTrackOrder = (order) => {
    if (!canTrackOrders) {
      alert("You don't have permission to track orders");
      return;
    }
    setSelectedOrder(order);
    setShowTrackingModal(true);
  };

  const handleCloseModal = () => {
    setShowTrackingModal(false);
    setSelectedOrder(null);
  };

  // ─── Styles ───
  const styles = {
    container: { 
      height: "100vh", 
      overflow: "hidden", 
      display: "flex", 
      flexDirection: "column",
      background: theme.background, 
      fontFamily: "'Segoe UI', sans-serif", 
      padding: isMobile ? "8px 12px" : isTablet ? "12px 20px" : "12px 24px",
      transition: "background 0.3s ease"
    },
    contentWrapper: { 
      width: "100%", 
      height: "100%", 
      display: "flex", 
      flexDirection: "column", 
      overflow: "hidden" 
    },
    header: { 
      display: "flex", 
      flexDirection: isMobile ? "column" : "row", 
      justifyContent: "space-between", 
      alignItems: isMobile ? "stretch" : "center", 
      marginBottom: "8px", 
      gap: isMobile ? "8px" : "12px", 
      flexShrink: 0
    },
    titleSection: { flex: isMobile ? "auto" : 1, display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" },
    title: { fontSize: isMobile ? "18px" : "20px", fontWeight: 700, color: theme.textPrimary, margin: 0, transition: "color 0.3s ease" },
    subtitle: { fontSize: "11px", color: theme.textMuted, marginTop: "2px", transition: "color 0.3s ease" },
    backBtn: { 
      display: "flex", 
      alignItems: "center", 
      gap: "6px", 
      padding: "6px 14px", 
      borderRadius: "8px", 
      border: `1px solid ${theme.border}`, 
      background: "transparent", 
      color: theme.primary, 
      cursor: "pointer", 
      fontSize: "12px", 
      fontWeight: 500,
      transition: "all 0.3s ease",
      whiteSpace: "nowrap"
    },
    filterBadge: { display: "inline-flex", alignItems: "center", gap: "6px", padding: "4px 12px", borderRadius: "20px", fontSize: "11px", fontWeight: 600, background: theme.primaryLight, color: theme.primary, border: `1px solid ${theme.border}`, transition: "all 0.3s ease" },
    searchWrapper: { position: "relative", width: isMobile ? "100%" : isTablet ? "250px" : "300px", flexShrink: 0 },
    searchIcon: { position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: theme.primary, transition: "color 0.3s ease" },
    searchInput: { width: "100%", padding: "8px 12px 8px 36px", background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: "10px", fontSize: "12px", color: theme.textPrimary, outline: "none", boxSizing: "border-box", transition: "all 0.3s ease" },
    quickActionsRow: { display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "8px", padding: "8px 14px", background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: "12px", flexShrink: 0, transition: "all 0.3s ease" },
    quickActionBtn: (isActive) => ({ 
      display: "flex", alignItems: "center", gap: "6px", padding: "6px 12px", borderRadius: "30px", fontSize: "11px", fontWeight: 600, cursor: "pointer", border: "none", 
      background: isActive ? theme.primary : theme.primaryLight, 
      color: isActive ? "#ffffff" : theme.textMuted,
      transition: "all 0.3s ease"
    }),
    quickBtnCount: { background: "rgba(0,0,0,0.1)", padding: "2px 6px", borderRadius: "16px", fontSize: "9px", fontWeight: 600, color: "inherit" },
    filtersRow: { display: "flex", flexWrap: "wrap", gap: "12px", marginBottom: "8px", paddingBottom: "8px", borderBottom: `1px solid ${theme.border}`, flexShrink: 0, transition: "all 0.3s ease" },
    filterGroup: { display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap", backgroundColor: theme.primaryLight, padding: "4px 10px", borderRadius: "10px", transition: "all 0.3s ease" },
    filterLabel: { fontSize: "10px", fontWeight: 700, color: theme.primary, textTransform: "uppercase", transition: "color 0.3s ease" },
    filterBtn: (isActive) => ({ padding: "3px 8px", borderRadius: "14px", fontSize: "10px", fontWeight: 500, background: isActive ? theme.primary : "transparent", color: isActive ? "#fff" : theme.textMuted, border: isActive ? `1px solid ${theme.primary}` : `1px solid ${theme.border}`, cursor: "pointer", transition: "all 0.3s ease" }),
    selectionBar: { display: "flex", alignItems: "center", gap: "10px", background: theme.primaryLight, border: `1px solid ${theme.border}`, borderRadius: "8px", padding: "6px 12px", marginBottom: "8px", flexWrap: "wrap", flexShrink: 0, transition: "all 0.3s ease" },
    tableCard: { 
      flex: 1, 
      display: "flex", 
      flexDirection: "column", 
      background: theme.surface, 
      border: `1px solid ${theme.border}`, 
      borderRadius: "12px", 
      overflow: "hidden", 
      boxShadow: `0 4px 20px ${theme.primaryLight}`,
      transition: "all 0.3s ease"
    },
    tableWrapper: {
      flex: 1,
      overflow: "auto",
      scrollbarWidth: "none",
      msOverflowStyle: "none",
    },
    table: { width: "100%", borderCollapse: "collapse", minWidth: "1000px" },
    th: { padding: "12px 8px", textAlign: "left", fontSize: "10px", fontWeight: 700, color: theme.primary, borderBottom: `1px solid ${theme.border}`, background: theme.primaryLight, whiteSpace: "nowrap", position: "sticky", top: 0, zIndex: 10, transition: "all 0.3s ease" },
    td: { padding: "10px 8px", fontSize: "11px", color: theme.textPrimary, borderBottom: `1px solid ${theme.border}`, wordBreak: "break-word", transition: "all 0.3s ease" },
    pagination: { display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: "center", justifyContent: "space-between", gap: isMobile ? "10px" : "0", padding: "10px 16px", borderTop: `1px solid ${theme.border}`, background: theme.primaryLight, flexShrink: 0, transition: "all 0.3s ease" },
    paginationInfo: { fontSize: "11px", color: theme.textMuted, transition: "color 0.3s ease" },
    paginationButtons: { display: "flex", gap: "4px", flexWrap: "wrap" },
    pageBtn: (disabled, isActive) => ({ width: "28px", height: "28px", borderRadius: "6px", border: isActive ? `1px solid ${theme.primary}` : `1px solid ${theme.border}`, background: isActive ? theme.primaryLight : theme.surface, color: isActive ? theme.primary : disabled ? theme.textMuted : theme.textPrimary, cursor: disabled ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px", transition: "all 0.3s ease" }),
    loadingContainer: { display: "flex", justifyContent: "center", alignItems: "center", height: "100%", color: theme.textMuted, fontSize: "12px", flexDirection: "column", gap: "12px" },
    actionButton: { background: "none", border: "none", cursor: "pointer", padding: "6px", borderRadius: "6px", display: "inline-flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s", color: theme.primary },
    actionButtonsWrapper: { display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" },
    exportSection: { display: "flex", gap: "6px", alignItems: "center", flexWrap: "wrap" },
    exportBtn: { 
      display: "flex", 
      alignItems: "center", 
      gap: "4px", 
      padding: "6px 12px", 
      borderRadius: "8px", 
      border: `1px solid ${theme.border}`, 
      background: theme.surface, 
      color: theme.textPrimary, 
      cursor: "pointer", 
      fontSize: "11px", 
      fontWeight: 500,
      transition: "all 0.3s ease",
      whiteSpace: "nowrap"
    },
  };

  const Spinner = () => (
    <div style={{ width: "30px", height: "30px", border: `3px solid ${theme.border}`, borderTopColor: theme.primary, borderRadius: "50%", animation: "spin 1s linear infinite", transition: "all 0.3s ease" }} />
  );

  // ─── PERMISSION DENIED ───
  if (!canViewOrders && !loading) {
    return (
      <div style={{ 
        height: "100vh", 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center",
        background: theme.background,
        overflow: "hidden",
        transition: "background 0.3s ease"
      }}>
        <div style={{
          background: theme.surface,
          padding: "40px",
          borderRadius: "16px",
          textAlign: "center",
          maxWidth: "400px",
          border: `1px solid ${theme.border}`,
          boxShadow: `0 4px 20px ${theme.primaryLight}`,
          transition: "all 0.3s ease"
        }}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>🔒</div>
          <h2 style={{ color: theme.primary, marginBottom: "8px", transition: "color 0.3s ease" }}>Access Denied</h2>
          <p style={{ color: theme.textMuted, fontSize: "14px" }}>
            You don't have permission to view orders. 
            Required permission: <strong>orders.view</strong>
          </p>
        </div>
      </div>
    );
  }

  // ─── LOADING ───
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <Spinner />
          <span>Loading orders...</span>
        </div>
        <style>{`
          @keyframes spin { to { transform: rotate(360deg); } }
          .no-scrollbar::-webkit-scrollbar { display: none; }
          .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        `}</style>
      </div>
    );
  }

  // ─── RENDER ───
  return (
    <div style={styles.container}>
      <style>{`
        button:hover { opacity: 0.85; }
        [data-tooltip] { position: relative; cursor: pointer; }
        [data-tooltip]:before {
          content: attr(data-tooltip);
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          padding: 4px 8px;
          background: ${theme.primary};
          color: #fff;
          font-size: 10px;
          font-weight: 500;
          white-space: nowrap;
          border-radius: 6px;
          display: none;
          z-index: 100;
          margin-bottom: 8px;
          pointer-events: none;
        }
        [data-tooltip]:hover:before { display: block; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        body { overflow: hidden; }
      `}</style>

      <div style={styles.contentWrapper}>
        <div style={styles.header}>
          <div style={styles.titleSection}>
            <button 
              onClick={() => navigate('/dashboard/orders')} 
              style={styles.backBtn}
            >
              <BackIcon /> Back
            </button>
            <div>
              <h1 style={styles.title}>
                {isPreFiltered ? filterTitle : "All Orders"}
              </h1>
              <p style={styles.subtitle}>
                {isPreFiltered 
                  ? `Showing ${orders.length} ${filterTitle.toLowerCase()}`
                  : "Manage and track all customer orders"}
              </p>
            </div>
            {isPreFiltered && (
              <span style={styles.filterBadge}>
                <span>📋</span> {orders.length} orders
              </span>
            )}
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
            {/* Export Buttons */}
            {canExportOrders && (
              <div style={styles.exportSection}>
                <button 
                  style={styles.exportBtn}
                  onClick={exportToPDF}
                  disabled={exporting || filteredOrders.length === 0}
                >
                  <PDFIcon /> {exporting ? 'Exporting...' : 'PDF'}
                </button>
                <button 
                  style={styles.exportBtn}
                  onClick={exportToExcel}
                  disabled={exporting || filteredOrders.length === 0}
                >
                  <ExcelIcon /> Excel
                </button>
                {selected.size > 0 && (
                  <button 
                    style={{...styles.exportBtn, background: theme.primary, color: '#fff', borderColor: theme.primary}}
                    onClick={exportSelected}
                    disabled={exporting}
                  >
                    <DownloadIcon /> Export Selected ({selected.size})
                  </button>
                )}
              </div>
            )}
            <div style={styles.searchWrapper}>
              <span style={styles.searchIcon}><SearchIcon /></span>
              <input 
                type="text" 
                style={styles.searchInput} 
                placeholder="Search by order ID, restaurant, customer..." 
                value={search} 
                onChange={(e) => setSearch(e.target.value)} 
              />
            </div>
          </div>
        </div>

        {showCancelledAmount && (
          <div style={{
            marginBottom: "10px", padding: "12px 16px", borderRadius: "10px",
            background: `${theme.error}12`, border: `1px solid ${theme.error}35`,
            color: theme.error, fontSize: "14px", fontWeight: 800,
          }}>
            Total Cancelled Amount: ₹{formatCurrency(cancelledAmount)}
          </div>
        )}

        {!isPreFiltered && (
          <div style={styles.quickActionsRow}>
            <button style={styles.quickActionBtn(quickFilter === "all")} onClick={() => { setQuickFilter("all"); setCurrentPage(1); }}>📋 All <span style={styles.quickBtnCount}>{counts.all}</span></button>
            <button style={styles.quickActionBtn(quickFilter === "new")} onClick={() => { setQuickFilter("new"); setCurrentPage(1); }}>🆕 New <span style={styles.quickBtnCount}>{counts.new}</span></button>
            <button style={styles.quickActionBtn(quickFilter === "accepted_preparing_out")} onClick={() => { setQuickFilter("accepted_preparing_out"); setCurrentPage(1); }}>🍳 Active <span style={styles.quickBtnCount}>{counts.accepted_preparing_out}</span></button>
            <button style={styles.quickActionBtn(quickFilter === "delivered")} onClick={() => { setQuickFilter("delivered"); setCurrentPage(1); }}>✅ Delivered <span style={styles.quickBtnCount}>{counts.delivered}</span></button>
            <button style={styles.quickActionBtn(quickFilter === "cancelled")} onClick={() => { setQuickFilter("cancelled"); setCurrentPage(1); }}>❌ Cancelled <span style={styles.quickBtnCount}>{counts.cancelled}</span></button>
          </div>
        )}

        <div style={styles.filtersRow}>
          <div style={styles.filterGroup}>
            <span style={styles.filterLabel}>Status:</span>
            <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
              {["All", "New", "Active", "Delivered", "Cancelled", "Preparing", "Ready", "Out for Delivery"].map(s => (
                <button key={s} style={styles.filterBtn(statusFilter === s)} onClick={() => { setStatusFilter(s); setCurrentPage(1); }}>{s}</button>
              ))}
            </div>
          </div>
          <div style={styles.filterGroup}>
            <span style={styles.filterLabel}>Payment:</span>
            <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
              {["All", "COD", "Online"].map(p => (
                <button key={p} style={styles.filterBtn(paymentFilter === p)} onClick={() => { setPaymentFilter(p); setCurrentPage(1); }}>{p}</button>
              ))}
            </div>
          </div>
        </div>

        {selected.size > 0 && (
          <div style={styles.selectionBar}>
            <span style={{ fontSize: "11px", color: theme.primary, fontWeight: 600 }}>✓ {selected.size} order{selected.size > 1 ? "s" : ""} selected</span>
            <div style={{ flex: 1 }} />
            <button onClick={() => setSelected(new Set())} style={{ padding: "4px 10px", borderRadius: "6px", border: `1px solid ${theme.border}`, background: theme.surface, color: theme.primary, fontSize: "10px", cursor: "pointer", transition: "all 0.2s" }}>Clear</button>
          </div>
        )}

        <div style={styles.tableCard}>
          <div style={styles.tableWrapper} className="no-scrollbar">
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>
                    <Checkbox 
                      checked={selected.size === paginatedOrders.length && paginatedOrders.length > 0} 
                      onChange={toggleAll} 
                      theme={theme} 
                    />
                  </th>
                  <th style={styles.th}>#</th>
                  <th style={styles.th}>Order ID</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Date & Time</th>
                  <th style={styles.th}>Restaurant</th>
                  <th style={styles.th}>Customer</th>
                  <th style={styles.th}>Payment</th>
                  <th style={styles.th}>Method</th>
                  <th style={styles.th}>Restro</th>
                  <th style={styles.th}>Driver</th>
                  <th style={styles.th}>Amount</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedOrders.length === 0 ? (
                  <tr>
                    <td colSpan="13" style={{ padding: "60px", textAlign: "center", color: theme.textMuted, fontSize: "13px" }}>
                      🍽️ No orders found
                    </td>
                  </tr>
                ) : (
                  paginatedOrders.map((order, idx) => {
                    const sn = totalFilteredCount - ((currentPage - 1) * itemsPerPage + idx);
                    return (
                      <tr 
                        key={order.id} 
                        style={{ 
                          background: selected.has(order.id) 
                            ? theme.primaryLight 
                            : "transparent",
                          transition: "background 0.2s"
                        }}
                      >
                        <td style={styles.td}>
                          <Checkbox 
                            checked={selected.has(order.id)} 
                            onChange={() => toggleOne(order.id)} 
                            theme={theme} 
                          />
                        </td>
                        <td style={styles.td}>{sn}</td>
                        <td style={styles.td}>
                          <span style={{ fontFamily: "monospace", fontWeight: 600, color: theme.primary, fontSize: "11px" }}>
                            {order.orderId}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <StatusBadge status={order.status} theme={theme} />
                        </td>
                        <td style={styles.td}>
                          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                            <span>{order.date}</span>
                            <span style={{ fontSize: "9px", color: theme.textMuted }}>
                              {order.time}
                            </span>
                          </div>
                        </td>
                        <td style={styles.td}>
                          <span style={{ fontWeight: 500, color: theme.textPrimary }}>
                            {order.restaurant}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <span>{order.user}</span>
                        </td>
                        <td style={styles.td}>
                          <PayStatusBadge status={order.pay_status} theme={theme} />
                        </td>
                        <td style={styles.td}>
                          <span style={{ fontSize: "10px", fontWeight: 500 }}>{order.pay_method || "COD"}</span>
                        </td>
                        <td style={styles.td}>
                          <StatusBadge status={order.order_status_restro} theme={theme} />
                        </td>
                        <td style={styles.td}>
                          <StatusBadge status={order.order_status_driver} theme={theme} />
                        </td>
                        <td style={styles.td}>
                          <span style={{ fontWeight: 600, color: theme.success, fontSize: "11px" }}>
                            ₹{order.amount}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <div style={styles.actionButtonsWrapper}>
                            <button 
                              data-tooltip="Order Details" 
                              onClick={() => handleViewFullDetails(order)} 
                              style={styles.actionButton}
                            >
                              <DetailsIcon />
                            </button>
                            
                            {canTrackOrders && (
                              <button 
                                data-tooltip="Track Order" 
                                onClick={() => handleTrackOrder(order)} 
                                style={styles.actionButton}
                              >
                                <TrackIcon />
                              </button>
                            )}
                            
                            {canEditOrders && (
                              <button 
                                data-tooltip="Edit Order" 
                                onClick={() => handleViewFullDetails2(order)} 
                                style={styles.actionButton}
                              >
                                <EditIcon />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {filteredOrders.length > 0 && totalPages > 1 && (
            <div style={styles.pagination}>
              <div style={styles.paginationInfo}>
                Showing {Math.min((currentPage - 1) * itemsPerPage + 1, filteredOrders.length)} - {Math.min(currentPage * itemsPerPage, filteredOrders.length)} of {filteredOrders.length} orders
              </div>
              <div style={styles.paginationButtons}>
                <button 
                  onClick={goToFirstPage} 
                  disabled={currentPage === 1} 
                  style={styles.pageBtn(currentPage === 1, false)}
                  title="First Page"
                >
                  ««
                </button>
                
                <button 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                  disabled={currentPage === 1} 
                  style={styles.pageBtn(currentPage === 1, false)}
                >
                  <ChevronLeftIcon />
                </button>
                
                {(() => {
                  const pages = [];
                  const maxVisible = isMobile ? 3 : 5;
                  let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                  let endPage = Math.min(totalPages, startPage + maxVisible - 1);
                  
                  if (endPage - startPage + 1 < maxVisible) {
                    startPage = Math.max(1, endPage - maxVisible + 1);
                  }
                  
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button 
                        key={i} 
                        onClick={() => setCurrentPage(i)} 
                        style={styles.pageBtn(false, currentPage === i)}
                      >
                        {i}
                      </button>
                    );
                  }
                  return pages;
                })()}
                
                <button 
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
                  disabled={currentPage === totalPages} 
                  style={styles.pageBtn(currentPage === totalPages, false)}
                >
                  <ChevronRightIcon />
                </button>
                
                <button 
                  onClick={goToLastPage} 
                  disabled={currentPage === totalPages} 
                  style={styles.pageBtn(currentPage === totalPages, false)}
                  title="Last Page"
                >
                  »»
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* OrderTrackingModal */}
      {showTrackingModal && selectedOrder && canTrackOrders && (
        <OrderTrackingModal 
          order={selectedOrder} 
          onClose={handleCloseModal}
          isDark={theme.name === "dark" || theme.name === "Dark"}
        />
      )}
    </div>
  );
};

export default OrderTable;
